import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { takeUntil, tap, withLatestFrom } from 'rxjs';
import { AuthActions } from '../state/auth/auth.action-types';
import { ConnectivityActions } from '../state/connectivity/connectivity.actions';
import { AppState } from '../app.state';
import { organizationFeature } from '../state/organization/organization.feature';
import { userSettingsFeature } from '../state/user-settings/user-settings.feature';
import { AuthService } from '../../services/auth.service';
import { ConnectivityTimerService } from '@ui';
import { UserSettingsModels } from '@share-models';

export const registerUserActivity$ = createEffect(
  () => {
    const actions$ = inject(Actions);
    const store$ = inject(Store<AppState>);
    const authService = inject(AuthService);
    const inactivityService = inject(ConnectivityTimerService);

    return actions$.pipe(
      ofType(ConnectivityActions.registerUserActivity),
      withLatestFrom(
        store$.select(organizationFeature.selectActiveOrganization),
      ),
      tap(([, organization]) => {
        if (!authService.isLoggedIn()) {
          inactivityService.stopTimer();
          return;
        }

        const disableInactivity =
          organization?.settings?.disableInactivity ?? false;
        if (disableInactivity) {
          inactivityService.stopTimer();
          return;
        }

        // restart timer on user activity
        inactivityService.resetTimer();
        inactivityService.startTimer();
        store$.dispatch(
          ConnectivityActions.setIsInactive({ isInactive: false }),
        );
        store$.dispatch(
          ConnectivityActions.setInactivityTime({ inactivityTime: 0 }),
        );
      }),
    );
  },
  { functional: true, dispatch: false },
);

export const inactivityTicks$ = createEffect(
  () => {
    const store$ = inject(Store<AppState>);
    const authService = inject(AuthService);
    const inactivityService = inject(ConnectivityTimerService);
    const actions$ = inject(Actions);

    return inactivityService.inactivityTime$.pipe(
      withLatestFrom(
        store$.select(organizationFeature.selectActiveOrganization),
        store$.select(userSettingsFeature.selectUserSettings),
      ),
      tap(([time, organization, settings]) => {
        store$.dispatch(
          ConnectivityActions.setInactivityTime({ inactivityTime: time }),
        );
        // stop tracking if not logged in or org disables inactivity
        const disableInactivity =
          organization?.settings?.disableInactivity ?? false;
        if (disableInactivity || !authService.isLoggedIn()) {
          inactivityService.stopTimer();
          store$.dispatch(
            ConnectivityActions.setIsInactive({ isInactive: false }),
          );
          return;
        }

        const autoLogoutEnabled = settings?.autoLogoutEnabled ?? false;
        const autoLogoutCounter = settings?.autoLogout ?? 0;
        const triggerTime =
          autoLogoutCounter * 60 -
          UserSettingsModels.INACTIVITY_COUNTDOWN_SECONDS;

        // open auto-logout modal 30s before the user-configured timeout
        if (
          authService.isLoggedIn() &&
          !disableInactivity &&
          autoLogoutEnabled &&
          triggerTime > 0 &&
          time === triggerTime
        ) {
          const secondsLeft = Math.max(
            autoLogoutCounter * 60 - time,
            UserSettingsModels.INACTIVITY_COUNTDOWN_SECONDS,
          );
          store$.dispatch(
            ConnectivityActions.showInactivityModal({
              secondsLeft: Math.min(
                secondsLeft,
                UserSettingsModels.INACTIVITY_COUNTDOWN_SECONDS,
              ),
            }),
          );
        }

        // mark inactive at the hard timeout (no forced logout)
        if (
          time >= UserSettingsModels.APP_INACTIVITY_TIME &&
          authService.isLoggedIn()
        ) {
          store$.dispatch(
            ConnectivityActions.setIsInactive({ isInactive: true }),
          );
          inactivityService.stopTimer();
        }
      }),
      takeUntil(actions$.pipe(ofType(AuthActions.logout))),
    );
  },
  { functional: true, dispatch: false },
);

export const logoutCleanup$ = createEffect(
  () => {
    const actions$ = inject(Actions);
    const inactivityService = inject(ConnectivityTimerService);

    return actions$.pipe(
      ofType(AuthActions.logout),
      tap(() => inactivityService.stopTimer()),
    );
  },
  { functional: true, dispatch: false },
);
